/**
 * Docked Operations shell — Salesforce-style shift (not overlay), AWS-style history.
 * Reuses ConversationShell; TMOS_OPERATIONS context; bound page contract.
 * Project pages reopen the team thread; CRM reopens the current user's thread.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PlusIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { OperationsHonestyHint } from './OperationsHonestyHint';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  ConversationShell,
  useConversation,
  readConversationTitle,
} from '@/components/ai-studio/conversation';
import {
  createConversation,
  getConversation,
  listConversationMessages,
  sendConversationMessage,
} from '@/services/aiStudioService';
import api from '@/services/api';
import type { ActiveConversationContext } from '@/types/aiStudio';
import { useOperationsPane } from './operationsContext';
import {
  crmTabFromPath,
  isTransformationDetailRoute,
  operationsBindKey,
  operationsHonestyCopy,
  transformationIdFromPath,
} from './pageBind';

const PANE_WIDTH_PX = 380;

export const OperationsPane: React.FC = () => {
  const { t, translateWithFallback } = useLanguage();
  const location = useLocation();
  const { isOpen, setOpen, canUse, notifyPageMutated } = useOperationsPane();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [title, setTitle] = useState('Operations');
  const [undoBusy, setUndoBusy] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);

  const pageBind = useMemo(
    () => ({
      route: location.pathname,
      tab: crmTabFromPath(location.pathname) || undefined,
      selectionId: transformationIdFromPath(location.pathname),
    }),
    [location.pathname]
  );
  const bindKey = operationsBindKey(location.pathname);

  const honesty = operationsHonestyCopy(location.pathname);
  const onProject = isTransformationDetailRoute(location.pathname);

  const transport = useMemo(
    () => ({
      send: (
        id: string,
        content: string,
        options?: { modelConfigurationId?: string | null }
      ) => sendConversationMessage(id, content, { ...options, pageBind }),
    }),
    [pageBind]
  );

  const {
    messages,
    sending,
    error,
    setMessages,
    send,
  } = useConversation({
    conversationId,
    transport,
    sendErrorMessage: t('aiStudio.conversation.sendFailed'),
    onTurnComplete: () => {
      notifyPageMutated();
    },
  });

  const loadThread = useCallback(
    async (forceNew = false) => {
      const created = await createConversation({
        contextType: 'TMOS_OPERATIONS',
        contextRef: { page_bind: pageBind },
        forceNew,
      });
      setConversationId(created.id);
      setTitle(readConversationTitle(created.shellMetadata) || 'Operations');
      const history = await listConversationMessages(created.id);
      setMessages(history);
      setBootError(null);
    },
    [pageBind, setMessages]
  );

  useEffect(() => {
    setConversationId(null);
    setMessages([]);
  }, [bindKey, setMessages]);

  useEffect(() => {
    if (!isOpen || !canUse) return;
    let cancelled = false;
    (async () => {
      try {
        await loadThread(false);
      } catch (err) {
        if (!cancelled) {
          setBootError(err instanceof Error ? err.message : t('aiStudio.conversation.sendFailed'));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, canUse, bindKey, loadThread, t]);

  const handleNewThread = async () => {
    try {
      await loadThread(true);
    } catch (err) {
      setBootError(err instanceof Error ? err.message : t('aiStudio.conversation.sendFailed'));
    }
  };

  const handleUndo = async (userMessageId: string) => {
    if (!conversationId || undoBusy) return;
    setUndoBusy(true);
    try {
      await api.post(`/ai-studio/conversations/${conversationId}/operations/undo`, {
        userMessageId,
      });
      const fresh = await getConversation(conversationId);
      setTitle(readConversationTitle(fresh.shellMetadata) || title);
      const history = await listConversationMessages(conversationId);
      setMessages(history);
      notifyPageMutated();
    } catch {
      setBootError(t('operations.pane.undoFailed'));
    } finally {
      setUndoBusy(false);
    }
  };

  if (!canUse || !isOpen) return null;

  const context: ActiveConversationContext = {
    contextType: 'TMOS_OPERATIONS',
    scopeLabel: t('operations.pane.title'),
    status: 'open',
  };

  return (
    <aside
      className="flex flex-col shrink-0 border-l border-[var(--color-border)] bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]"
      style={{ width: PANE_WIDTH_PX }}
      aria-label={t('operations.pane.title')}
    >
      <header className="flex items-center gap-1 border-b border-[var(--color-border)] px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="p-1 rounded hover:bg-[var(--color-surface-bg)]"
          title={t('operations.pane.close')}
          aria-label={t('operations.pane.close')}
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold truncate">{t('operations.pane.title')}</div>
          <div className="text-[11px] leading-tight opacity-80 truncate">
            {translateWithFallback(honesty.boundAsKey, honesty.boundAsFallback)}
            {onProject
              ? ` · ${translateWithFallback('operations.pane.teamThread', 'Shared with this project team')}`
              : ''}
          </div>
        </div>
        <OperationsHonestyHint pathname={location.pathname} />
        <button
          type="button"
          onClick={() => void handleNewThread()}
          className="p-1 rounded hover:bg-[var(--color-surface-bg)]"
          title={t('operations.pane.newThread')}
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </header>
      <p className="px-2 py-1 text-[10px] leading-snug bg-[var(--color-surface-bg)] border-b border-[var(--color-border)]">
        {t('operations.pane.testMode')}
      </p>
      {bootError && (
        <p className="px-2 py-1 text-xs text-[var(--color-error)]">{bootError}</p>
      )}
      <div className="flex min-h-0 flex-1 flex-col">
        <ConversationShell
          context={context}
          messages={messages}
          sending={sending}
          error={error}
          onSend={send}
          paneMode
          hideContextIndicator
          composerPlaceholder=""
          onUndoTurn={(id) => void handleUndo(id)}
          undoBusy={undoBusy}
        />
      </div>
    </aside>
  );
};
